import type { IconName } from '#/components/Icon/IconName';
import type { I18nFn } from '#/i18n/types/I18nFn';
import type { EmployerCityBucket } from '#/modules/salary/types/EmployerCityBucket';
import type { MainChartData } from '#/modules/salary/types/MainChartData';
import type { MainChartDetails } from '#/modules/salary/types/MainChartDetails';
import { invariant } from '#/utils/invariant';

function getYearsOfExperienceString(i18n: I18nFn, yearsOfExperience: number) {
  if (yearsOfExperience === 1) {
    return i18n('%(yearsOfExperience)s year of experience', {
      yearsOfExperience,
    });
  }

  return i18n('%(yearsOfExperience)s years of experience', {
    yearsOfExperience,
  });
}

function getLocationKeyInputText(
  i18n: I18nFn,
  city = '',
  state = '',
  cityBucket: EmployerCityBucket = 'NATIONAL',
) {
  const filterToTextMap = {
    CITY: `${city}, ${state}`,
    LOW: i18n('Cities Like Yours'),
    MEDIUM: i18n('Cities Like Yours'),
    HIGH: i18n('Cities Like Yours'),
    NATIONAL: i18n('All USA'),
  };

  return filterToTextMap[cityBucket];
}

export function getMainChartDetails(
  i18n: I18nFn,
  data: MainChartData,
  activeChart: 'estimatedRange' | 'submissions',
): MainChartDetails {
  invariant(data, 'chart data is required to get main chart details');

  const { filtersUsed, lineChart, submittedData } = data;

  return {
    estimatedRange: {
      description: i18n(
        'The "Estimated Range" curve in this visualization is a machine learning algorithm trained on survey submissions to predict salaries for different titles, locations, and experience.',
      ),
      yearsOfExperience: submittedData?.yearsOfExperience
        ? getYearsOfExperienceString(i18n, submittedData.yearsOfExperience)
        : i18n('Any number of years of experience'),
      mostCommonSalaryMin: lineChart?.sectionLimits?.middle68LowerBound ?? 0,
      mostCommonSalaryMax: lineChart?.sectionLimits?.middle68UpperBound ?? 0,
      jobTitle: submittedData?.jobTitleSelected,
      chartIcon: 'chart-bell-filled' as IconName,
      location:
        submittedData?.city && submittedData?.stateCode
          ? `${submittedData.city}, ${submittedData.stateCode}`
          : i18n('All USA'),
    },
    submissions: {
      mostCommonSalaryMin: lineChart?.sectionLimits?.middle68LowerBound ?? 0,
      mostCommonSalaryMax: lineChart?.sectionLimits?.middle68UpperBound ?? 0,
      description: i18n(
        'Submissions in this visualization represent individual salaries submitted by nonprofit professionals from 2018 to the present, adjusted for inflation.',
      ),
      yearsOfExperience: filtersUsed?.yearsOfExperience
        ? i18n('%(yearsOfExperience)s years of experience', {
            yearsOfExperience: filtersUsed.yearsOfExperience.join('-'),
          })
        : i18n('Any number of years of experience'),
      jobTitle: submittedData?.jobTitleSelected,
      chartIcon: 'graph-filled' as IconName,
      location: getLocationKeyInputText(
        i18n,
        submittedData?.city,
        submittedData?.stateCode,
        filtersUsed?.cityBucket,
      ),
    },
  }[activeChart];
}
